import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Dropdown, InputNumber, Space } from 'antd';
import { useCallback, useMemo, useState } from 'react';

const SERIAL_MENU_KEY = 'serial-input';

/** 抽屉内按项目名称拖拽排序的单行（@dnd-kit） */
export default function ProjectNameSortableRow({ id, name, serialNumber, totalCount, isFirst, isLast, onQuickMove, onMoveToSerialNumber }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [serialInput, setSerialInput] = useState(serialNumber ?? 1);

    const showQuickActions = onQuickMove || onMoveToSerialNumber;

    const submitSerialMove = useCallback(() => {
        const target = Number(serialInput);
        if (!Number.isFinite(target) || !onMoveToSerialNumber) {
            return;
        }
        onMoveToSerialNumber(target);
        setDropdownOpen(false);
    }, [onMoveToSerialNumber, serialInput]);

    const stopMenuPointer = useCallback(event => {
        event.stopPropagation();
    }, []);

    const quickMoveMenuItems = useMemo(() => {
        if (!onQuickMove) {
            return [];
        }
        return [
            { key: 'top', label: '移到最前', disabled: isFirst },
            { key: 'bottom', label: '移到最后', disabled: isLast },
        ];
    }, [isFirst, isLast, onQuickMove]);

    const sortMenuItems = useMemo(() => {
        const items = [...quickMoveMenuItems];
        if (onMoveToSerialNumber) {
            if (items.length) {
                items.push({ type: 'divider' });
            }
            items.push({
                key: SERIAL_MENU_KEY,
                label: (
                    <div className="med-project-name-sort-serial-menu-item" onClick={stopMenuPointer} onPointerDown={stopMenuPointer} onKeyDown={stopMenuPointer}>
                        <span className="med-project-name-sort-serial-label">移动到序号</span>
                        <Space size={8} className="med-project-name-sort-serial-controls">
                            <InputNumber min={1} max={totalCount ?? 1} precision={0} size="small" value={serialInput} onChange={value => setSerialInput(value ?? serialNumber ?? 1)} onPressEnter={submitSerialMove} style={{ width: 72 }} />
                            <Button type="primary" size="small" onClick={submitSerialMove}>
                                确定
                            </Button>
                        </Space>
                    </div>
                ),
            });
        }
        return items;
    }, [quickMoveMenuItems, onMoveToSerialNumber, serialInput, serialNumber, stopMenuPointer, submitSerialMove, totalCount]);

    const onSortMenuClick = ({ key }) => {
        if (key === SERIAL_MENU_KEY) {
            return;
        }
        onQuickMove?.(key);
        setDropdownOpen(false);
    };

    const stopDragPointer = event => {
        event.stopPropagation();
    };

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} className="med-project-sortable-row med-project-name-sort-item">
            <div className="med-project-name-sort-drag-zone" {...listeners} aria-label="拖动排序">
                {serialNumber != null ? <span className="med-project-name-sort-index-outer">{serialNumber}</span> : null}
                <div className="med-project-name-sort-row">
                    <span className="med-project-name-sort-handle">
                        <i className="bi bi-grip-vertical"></i>
                    </span>
                    <span className="med-project-name-sort-label">{name}</span>
                    {showQuickActions ? (
                        <span className="med-project-name-sort-more" onPointerDown={stopDragPointer}>
                            <Dropdown
                                trigger={['click']}
                                open={dropdownOpen}
                                onOpenChange={open => {
                                    setDropdownOpen(open);
                                    if (open && serialNumber != null) {
                                        setSerialInput(serialNumber);
                                    }
                                }}
                                menu={{ items: sortMenuItems, onClick: onSortMenuClick }}
                            >
                                <Button type="text" size="small" aria-label="排序操作">
                                    <i className="bi bi-three-dots"></i>
                                </Button>
                            </Dropdown>
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
